import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Theme, alpha } from "@mui/material/styles";
import React from "react";

import DerivedTag from "../Detail/DerivedTag";
import EntityLink from "../Detail/EntityLink";
import { RelationGroup } from "../Detail/GroupedRelationList";
import SourceMarks from "../Detail/SourceMarks";
import { columnHeader, quiet } from "../Detail/claimCells";

const COLUMNS = ["Product", "Summary", "Src"];

// The group rows: small capitals on a faint tint, as the bands of the claim
// table
const bandCell = {
  ...quiet,
  backgroundColor: (theme: Theme) => alpha(theme.palette.common.white, 0.03),
  fontSize: "0.6875rem",
  fontWeight: 500,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  py: "5px",
} as const;

/**
 * The products of an incident as one table, grouped by how they are in it:
 * a band row per group, then a row per product with its summary and its
 * source marks. A derived relation is tagged beside the name. A group
 * without a product is left out; renders nothing when all are.
 */
const IncidentProductTable: React.FC<{ groups: RelationGroup[] }> = ({
  groups,
}) => {
  const shown = groups.filter((group) => group.items.length > 0);
  if (shown.length === 0) {
    return null;
  }

  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {COLUMNS.map((column) => (
              <TableCell key={column} sx={columnHeader}>
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {shown.map((group) => (
          // One body per group, named by it
          <TableBody key={group.label} aria-label={group.label}>
            <TableRow>
              <TableCell colSpan={COLUMNS.length} sx={bandCell}>
                {group.label}
              </TableCell>
            </TableRow>
            {group.items.map((item, index) => (
              <TableRow
                key={`${item.entity.id}-${index}`}
                sx={{ verticalAlign: "top" }}
              >
                <TableCell>
                  <EntityLink
                    id={item.entity.id}
                    type={item.entity.type}
                    name={item.entity.name}
                  />
                  {item.derived && (
                    <>
                      {" "}
                      <DerivedTag basis={item.derived.basis} />
                    </>
                  )}
                </TableCell>
                <TableCell sx={quiet}>{item.note}</TableCell>
                <TableCell>
                  <SourceMarks sources={item.sources} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        ))}
      </Table>
    </TableContainer>
  );
};

export default IncidentProductTable;
